"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import ProductCardDetails from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardDetails"
import { getImageSrc } from "@/app/(site)/_utils/getImageSrc"
import { getLangCode } from "@/app/(site)/_utils/misc"
import {
    areAllVariantsOutOfStock,
    getSortedColorSwatches
} from "@/lib/content-service/helpers/transformProductData"
import {
    ProductData,
    RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import { cn } from "@/lib/utils"
import placeholder from "@assets/images/placeholder.png"
import { useCallback, useEffect, useState } from "react"

const placeholderUrl = placeholder.src

type ProductCardProps = {
    product: ProductData
    lazyLoad?: boolean
    isBigCard?: boolean
    showBenefits?: boolean
    params: { locale: string }
}

export default function ProductCard({
    product,
    lazyLoad = false,
    isBigCard = false,
    showBenefits,
    params
}: ProductCardProps) {
    const langCode = getLangCode(params.locale) || "en"

    const allVariants: ProductData[] | RelatedVariant[] = [
        product,
        ...(product.relatedVariants || [])
    ]
    const [currentVariant, setCurrentVariant] = useState<ProductData | RelatedVariant>(
        product
    )

    const {
        name,
        price,
        priceBeforeDiscount,
        showAsOnSale,
        uri,
        benefitList,
        media,
        available,
        combinedBadges
    } = currentVariant || {}

    const sortedSwatches = getSortedColorSwatches(allVariants)
    const extraColorsCount = sortedSwatches.length > 6 ? sortedSwatches.length - 6 : 0
    const displayedSwatches = sortedSwatches.slice(0, 6)
    const allVariantOutOfStock = areAllVariantsOutOfStock(allVariants)

    /*
     * Check if `media` is a valid object containing at least one key.
     * Variants/products may have an empty media object (`{}`).
     * In this case, we should display a placeholder image.
     */
    const hasMedia = !!media && Object.keys(media).length > 0
    const selectedMedia = isBigCard && hasMedia ? media.standard : media.small

    const initialHoverImage =
        selectedMedia?.length > 1 ? selectedMedia[1]?.url : selectedMedia?.[0]?.url

    const initialImage = hasMedia
        ? typeof window !== "undefined"
            ? getImageSrc(window.innerWidth, media)
            : getImageSrc(1440, media) // Fallback for SSR
        : placeholderUrl

    const [imageSrc, setImageSrc] = useState<string>(initialImage)
    const [hoverImageSrc, setHoverImageSrc] = useState(initialHoverImage)

    const updateImages = useCallback(() => {
        if (!hasMedia) {
            setImageSrc(placeholderUrl)
            setHoverImageSrc(placeholderUrl)
            return
        }
        setImageSrc(
            isBigCard ? media.standard[0].url : getImageSrc(window.innerWidth, media)
        )
        const secondSmall = media.small?.[1]?.url
        const secondStandard = media.standard?.[1]?.url
        const nextImage =
            window.innerWidth <= 1440 && !isBigCard ? secondSmall : secondStandard
        setHoverImageSrc(nextImage)
    }, [media, hasMedia, isBigCard])

    useEffect(() => {
        if (typeof window === "undefined") {
            return
        }
        updateImages()
        const handleResize = () => {
            updateImages()
        }
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [updateImages])

    const handleHoverImage = (isEntering?: boolean) => {
        if (isEntering && hoverImageSrc) {
            setImageSrc(hoverImageSrc)
        } else if (hasMedia) {
            setImageSrc(
                isBigCard ? media.standard[0].url : getImageSrc(window.innerWidth, media)
            )
        } else {
            setImageSrc(placeholderUrl) // Fallback when media is undefined
        }
    }

    const handleMouseLeaveCard = () => {}

    const handleChangeVariant = (relatedVariant?: RelatedVariant | undefined) => {
        if (relatedVariant) {
            setCurrentVariant(prevState => ({ ...prevState, ...relatedVariant }))
        } else {
            setCurrentVariant(product)
        }
    }

    return (
        <div
            className="group relative"
            data-search-result-id={product.search_result_id}
            data-product-listing-result-id={product.product_listing_result_id}
            data-recommendation-id={product.recommendation_id}
        >
            <DynamicLink href={`/product/${uri}`} prefetch={false}>
                <div
                    className="cursor-pointer bg-[#f4f5f5]"
                    onMouseEnter={() => handleHoverImage(true)}
                    onMouseLeave={() => handleHoverImage(false)}
                >
                    {!!combinedBadges?.length && (
                        <div className="absolute left-0 top-0 z-10 flex flex-wrap gap-2">
                            {combinedBadges.map((badge, idx) => (
                                <Badge key={idx} badge={badge} />
                            ))}
                        </div>
                    )}

                    <div className="relative mb-16 aspect-square overflow-hidden">
                        <BaseImage
                            fill
                            src={imageSrc}
                            alt={name}
                            loading={lazyLoad ? "lazy" : "eager"}
                            decoding={lazyLoad ? "async" : "sync"}
                            className={cn(
                                "object-contain transition-opacity duration-300",
                                available || !allVariantOutOfStock
                                    ? "opacity-100"
                                    : "opacity-30"
                            )}
                        />
                    </div>
                </div>
            </DynamicLink>
            <ProductCardDetails
                displayedSwatches={displayedSwatches}
                extraColorsCount={extraColorsCount}
                langCode={langCode}
                handleChangeVariant={handleChangeVariant}
                uri={uri}
                name={name}
                price={price}
                priceBeforeDiscount={priceBeforeDiscount}
                showAsOnSale={showAsOnSale}
                productBenefits={benefitList}
                available={available}
                showBenefits={showBenefits}
            />
        </div>
    )
}
